import { useEffect, useRef } from 'react';
import { clarity } from 'react-microsoft-clarity';
import * as Sentry from '@sentry/react';

/**
 * Hook personalizado para Microsoft Clarity
 *
 * Inicializa Clarity y gestiona la identificación de usuarios automáticamente.
 *
 * @param {Object} options - Opciones de configuración
 * @param {string} options.clarityId - ID del proyecto de Clarity
 * @param {boolean} options.authenticated - Estado de autenticación del usuario
 * @param {string} options.username - Username del usuario autenticado
 * @param {string} [options.email] - Email del usuario (opcional)
 * @param {string} [options.fullName] - Nombre completo del usuario (opcional)
 * @param {Object} [options.customTags] - Tags personalizadas adicionales (opcional)
 *
 * @example
 * const { authenticated, username, email, fullName } = useComprasAuth();
 * useClarity({
 *   clarityId: import.meta.env.VITE_CLARITY_ID,
 *   authenticated,
 *   username,
 *   email,
 *   fullName,
 *   customTags: { role: 'admin' }
 * });
 */
export const useClarity = ({
  clarityId,
  authenticated = false,
  username = null,
  email = null,
  fullName = null,
  customTags = {},
}) => {
  const identifiedUserRef = useRef(null);

  /**
   * Inicializa Microsoft Clarity una sola vez
   */
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!clarityId) {
      Sentry.captureMessage("Microsoft Clarity ID not found", "warning");
      return;
    }

    // Evita múltiples inicializaciones
    if (window.__clarity_initialized) return;

    try {
      clarity.init(clarityId);
      window.__clarity_initialized = true;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "useClarity" },
        contexts: {
          clarity: {
            action: "initialization",
            clarityId: clarityId,
          },
        },
      });
    }
  }, [clarityId]);

  /**
   * Identifica al usuario autenticado en Clarity
   */
  useEffect(() => {
    if (!clarityId) return;
    if (!window.__clarity_initialized) return;
    if (!authenticated || !username) return;

    // Evita identificar al mismo usuario múltiples veces
    if (identifiedUserRef.current === username) return;

    try {
      // Identificar usuario con ID único
      clarity.identify(username);

      // Agregar tags estándar
      if (email) {
        clarity.setTag("email", email);
      }
      
      if (fullName) {
        clarity.setTag("name", fullName);
      }

      // Agregar tags personalizadas
      Object.entries(customTags).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          clarity.setTag(key, String(value));
        }
      });

      // Marcar como usuario identificado
      identifiedUserRef.current = username;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "useClarity" },
        contexts: {
          clarity: {
            action: "user_identification",
            username: username,
          },
        },
      });
    }
  }, [clarityId, authenticated, username, email, fullName, customTags]);

  return {
    isInitialized: window.__clarity_initialized || false,
    identifiedUser: identifiedUserRef.current,
  };
};

export default useClarity;
